/**
 * User Tracking Service Type Definitions
 */

// Event Types
export enum TrackingEventType {
  MOUSE_MOVE = 'mouse_move',
  MOUSE_CLICK = 'mouse_click',
  MOUSE_SCROLL = 'mouse_scroll',
  PAGE_VIEW = 'page_view',
  KEYSTROKE = 'keystroke',
  BOT_CHECK = 'bot_check'
}

// Base Event Interface
export interface BaseTrackingEvent {
  eventId: string;
  eventType: TrackingEventType;
  timestamp: number;
  sessionId: string;
  userId?: string;
  pageUrl: string;
  userAgent: string;
  screenResolution: {
    width: number;
    height: number;
  };
  viewport: {
    width: number;
    height: number;
  };
}

// Mouse Movement Event
export interface MouseMoveEvent extends BaseTrackingEvent {
  eventType: TrackingEventType.MOUSE_MOVE;
  data: {
    x: number;
    y: number;
    elementPath?: string;
    elementId?: string;
    elementClass?: string;
    elementTag?: string;
  };
}

// Mouse Click Event
export interface MouseClickEvent extends BaseTrackingEvent {
  eventType: TrackingEventType.MOUSE_CLICK;
  data: {
    x: number;
    y: number;
    button: number;
    elementPath: string;
    elementId?: string;
    elementClass?: string;
    elementTag?: string;
    elementText?: string;
  };
}

// Scroll Event
export interface ScrollEvent extends BaseTrackingEvent {
  eventType: TrackingEventType.MOUSE_SCROLL;
  data: {
    scrollX: number;
    scrollY: number;
    scrollHeight: number;
    scrollWidth: number;
  };
}

// Keystroke Event
export interface KeystrokeEvent extends BaseTrackingEvent {
  eventType: TrackingEventType.KEYSTROKE;
  data: {
    key: string;
    code: string;
    isComposing: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    metaKey: boolean;
    elementPath: string;
    elementId?: string;
    elementTag?: string;
    elementType?: string;
    isPasswordField: boolean;
    isSensitiveField: boolean;
  };
}

// Bot Check Event
export interface BotCheckEvent extends BaseTrackingEvent {
  eventType: TrackingEventType.BOT_CHECK;
  data: {
    isBot: boolean;
    isBotBypass: boolean;
    isBotBypassReason?: string;
    reason?: string;
    reasonCode?: string;
    score?: number;
    checkType: 'login' | 'signup' | 'action';
    action?: string;
    success: boolean;
  };
}

// Page View Event
export interface PageViewEvent extends BaseTrackingEvent {
  eventType: TrackingEventType.PAGE_VIEW;
  data: {
    url: string;
    title: string;
    referrer: string;
    pathname: string;
    search: string;
    hash: string;
  };
}

// Union type for all tracking events
export type TrackingEvent =
  | MouseMoveEvent
  | MouseClickEvent
  | ScrollEvent
  | KeystrokeEvent
  | PageViewEvent
  | BotCheckEvent;

// Event Batch
export interface EventBatch {
  batchId: string;
  events: TrackingEvent[];
  createdAt: number;
  retryCount: number;
  fromStorage?: boolean;
}

// Storage Types
export interface StorageAdapter {
  store(events: TrackingEvent[]): Promise<void>;
  retrieve(limit?: number): Promise<EventBatch[]>;
  remove(batchIds: string[]): Promise<void>;
  clear(): Promise<void>;
  destroy?(): void;
}

// Transmission Types
export interface TransmissionAdapter {
  send(batch: EventBatch): Promise<boolean>;
  isAvailable(): Promise<boolean>;
}

// Configuration Types
export interface TrackingConfig {
  enabled: boolean;
  debugMode: boolean;
  sessionTimeout: number;
  batchSize: number;
  flushInterval: number;
  maxRetries: number;
  retryDelay: number;
  samplingRate: number;
  mouseMoveSamplingRate: number;
  keystrokeSamplingRate: number;
  storageQuota: number;
  endpoints: {
    kinesis: {
      streamName: string;
      region: string;
    };
  };
  privacy: {
    excludeSelectors: string[];
    maskTextContent: boolean;
    maskInputValues: boolean;
    trackKeystrokes: boolean;
    keystrokePrivacy: {
      trackInPasswordFields: boolean;
      trackInSensitiveFields: boolean;
      maskKeyValues: boolean;
      allowedKeyCategories: KeyCategory[];
    };
  };
}

// Key Categories for privacy control
export enum KeyCategory {
  NAVIGATION = 'navigation',
  MODIFIER = 'modifier',
  FUNCTION = 'function',
  CONTROL = 'control',
  ALPHANUMERIC = 'alphanumeric',
  SPECIAL = 'special'
}
